import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import Skeleton from "react-loading-skeleton";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import SweetAlert2 from "react-sweetalert2";
import { ApiStatusTypeEnum } from "../../../enumeration/ApiStatusTypeEnum";
import { toAbsoluteUrl } from "../../../utils/CommonHelper";
import * as actions from "../redux/MemberAction";
import { TransferDialog } from "./transferDialog/TransferDialog";

export const MemberGameList = () => {
  const [urlSearchParams] = useSearchParams();
  const { memberId } = useParams();
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [selectedMemberId, setSelectedMemberId] = useState();
  const [password, setPassword] = useState("");
  const [passwordField, setPasswordField] = useState("");
  const [selCompanyName, setSelCompanyName] = useState(null);
  const [selBalance, setSelBalance] = useState(null);
  const [selCompanyCode, setSelCompanyCode] = useState(null);
  const [selGameRate, setSelGameRate] = useState(null);
  const [selGameUsername, setSelGameUsername] = useState(null);
  const [selGamePass, setSelGamePass] = useState(null);
  const [selGameUrl, setSelGameUrl] = useState(null);
  const [selGameExist, setGameExist] = useState(null);
  const [trigger, setTrigger] = useState(false);
  const memberState = useSelector(({ member }) => member, shallowEqual);
  const [swalProps, setSwalProps] = useState({});
  const [initialUsername, setInitialUsername] = useState("");
  const [initialAccUsername, setInitialAccUsername] = useState("");
  const [pathFromSearch, setPathFromSearch] = useState("");
  const [backToPagination, setBackToPagination] = useState(0);
  const [scrollPosition, setScrollPosition] = useState("");
  const [searchUsername, setSearchUsername] = useState("");
  const [currentAgentId, setCurrentAgentId] = useState("");
  const [currentUsername, setCurrentUsername] = useState("");
  const [memberBalancePermission, setMemberBalancePermission] = useState("");
  const [gameBalances, setGameBalances] = useState({});
  const loginIdRef = useRef("");
  const haveTransferPermissionRef = useRef();

  const location = useLocation();
  const queryParams = {
    action: "membergamelistV2",
    memberid: selectedMemberId,
    ...(urlSearchParams.get("agentid") && {
      loginid: loginIdRef.current,
      agentid: urlSearchParams.get("agentid"),
      projectid: urlSearchParams.get("projectid"),
      password,
    }),
  };

  const targetBalanceParams = (balanceonly, companycode) => ({
    action: "membertargetbalanceV2",
    memberid: selectedMemberId,
    balanceonly,
    companycode,
    ...(urlSearchParams.get("agentid") && {
      loginid: loginIdRef.current,
      agentid: urlSearchParams.get("agentid"),
      projectid: urlSearchParams.get("projectid"),
      password,
    }),
  });

  const fetchMemberGameList = () => {
    dispatch(
      actions.callMemberGameList(queryParams, urlSearchParams.get("agentid"))
    ).then((resp) => {
      resp = resp.data;
      if (resp.code === "0") {
        if (urlSearchParams.get("agentid")) {
          if (resp.loginid && resp.loginid !== "")
            loginIdRef.current = resp.loginid;
          if (
            resp.haveTransferPermission &&
            resp.haveTransferPermission !== ""
          ) {
            setMemberBalancePermission(resp.haveTransferPermission);
            haveTransferPermissionRef.current = resp.haveTransferPermission;
          }
        }
        const gamesWithNoBalance = resp.memberGameListsV2.filter(
          (game) => game.gamebalance === "--"
        );
        if (gamesWithNoBalance.length > 0) {
          gamesWithNoBalance.forEach((game) => {
            dispatch(
              actions.callMemberGameListBalance(
                targetBalanceParams("true", game.gamecode),
                urlSearchParams.get("agentid")
              )
            ).then((response) => {
              setGameBalances((prevBalances) => ({
                ...prevBalances,
                [game.gamecode]: response.data.balance,
              }));
            });
          });
        }
      } else {
        if (urlSearchParams.get("agentid")) {
          setPassword("");
          setPasswordField("");
          alert(resp.message);
        }
      }
    });
  };

  useEffect(() => {
    setSelectedMemberId(memberId ?? urlSearchParams.get("memberid"));
  }, [memberId]);

  useEffect(() => {
    if (selectedMemberId) {
      const initialUsername =
        location.state?.userName ?? urlSearchParams.get("agusername");
      const initialAccUsername =
        location.state?.state?.userName ?? urlSearchParams.get("agusername");
      const pathFromSearch = location.state?.searchName;
      const backToPagination =
        location.state?.state?.pagination ?? location.state?.pagination;
      const scrollPosition = location.state?.scrollPosition || 0;
      const searchUsername = location.state?.searchUsername;
      const memberBalancePermission =
        haveTransferPermissionRef.current ??
        (location.state?.state?.memberBalancePermission ||
          location.state?.memberBalancePermission);
      const currentAgentId =
        location.state?.state?.currentAgentId ?? urlSearchParams.get("agentid");
      const currentUsername =
        location.state?.state?.currentUsername ??
        urlSearchParams.get("agusername");

      setInitialUsername(initialUsername);
      setPathFromSearch(pathFromSearch);
      setBackToPagination(backToPagination);
      setScrollPosition(scrollPosition);
      setSearchUsername(searchUsername);
      setInitialAccUsername(initialAccUsername);
      setMemberBalancePermission(memberBalancePermission);
      setCurrentAgentId(currentAgentId);
      setCurrentUsername(currentUsername);

      fetchMemberGameList();
    }
  }, [selectedMemberId, password]);

  const handleCreateClick = (companyCode) => {
    const queryParams = {
      action: "creategameacct",
      memberid: selectedMemberId,
      companycode: companyCode,
      ...(urlSearchParams.get("agentid") && {
        loginid: loginIdRef.current,
        agentid: urlSearchParams.get("agentid"),
        projectid: urlSearchParams.get("projectid"),
        password,
      }),
    };

    dispatch(actions.createGameId(queryParams, urlSearchParams.get("agentid")))
      .then((resp) => {
        if (parseInt(resp.data.code) === ApiStatusTypeEnum.SUCCESS) {
          setSwalProps({
            show: true,
            icon: "success",
            title: intl.formatMessage({ id: "success" }),
            text: intl.formatMessage({ id: "createGameIdSuccess" }),
            onConfirm: () => {
              dispatch(
                actions.callMemberGameList(
                  queryParams,
                  urlSearchParams.get("agentid")
                )
              ).then((resp) => {
                const gamesWithNoBalance = resp.data.memberGameListsV2.filter(
                  (game) => game.gamebalance === "--"
                );
                gamesWithNoBalance.forEach((game) => {
                  dispatch(
                    actions.callMemberGameListBalance(
                      targetBalanceParams("true", game.gamecode),
                      urlSearchParams.get("agentid")
                    )
                  ).then((response) => {
                    setGameBalances((prevBalances) => ({
                      ...prevBalances,
                      [game.gamecode]: response.data.balance,
                    }));
                  });
                });
              });
            },
          });
        } else {
          setSwalProps({
            show: true,
            icon: "error",
            title: intl.formatMessage({ id: "error" }),
            text: resp.data.message,
          });
        }
      })
      .catch((error) => {
        setSwalProps({
          show: true,
          icon: "error",
          title: intl.formatMessage({ id: "error" }),
          text: error.message,
        });
      });
  };

  const handleRemovePromo = (companycode, gamename) => {
    setSwalProps({
      show: true,
      icon: "question",
      title: intl.formatMessage({ id: "confirmation" }),
      text: intl.formatMessage({ id: "questionRemovePromo" }, { gamename }),
      onConfirm: () => {
        dispatch(
          actions.removeMemberPromotion(
            {
              action: "removememberpromotion",
              memberid: selectedMemberId,
              companycode,
              ...(urlSearchParams.get("agentid") && {
                loginid: loginIdRef.current,
                agentid: urlSearchParams.get("agentid"),
                projectid: urlSearchParams.get("projectid"),
                password,
              }),
            },
            urlSearchParams.get("agentid")
          )
        ).then((resp) => {
          resp = resp.data;

          if (parseInt(resp.code) === ApiStatusTypeEnum.SUCCESS) {
            setSwalProps({
              show: true,
              icon: "success",
              title: intl.formatMessage({ id: "success" }),
              text: intl.formatMessage({ id: "removePromoSuccessfully" }),
            });
            fetchMemberGameList();

            dispatch(
              actions.fetchMemberGameBalance(
                targetBalanceParams("false", companycode),
                urlSearchParams.get("agentid")
              )
            );
          } else {
            setSwalProps({
              show: true,
              icon: "error",
              title: intl.formatMessage({ id: "error" }),
              text: resp.message,
            });
          }
        });
      },
    });
  };

  const containerStyle = {
    maxWidth: "400px",
    margin: "80px auto",
    padding: "30px",
    backgroundColor: "white",
    borderRadius: "10px",
    boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
  };

  const customCardClass =
    "rounded d-flex flex-column align-items-center justify-content-center py-3";

  if (!memberId && password === "")
    return (
      <div style={containerStyle}>
        <label htmlFor="password" className="form-label">
          Username
        </label>
        <input
          type="type"
          className="form-control mb-2"
          value={urlSearchParams.get("agusername")}
          disabled
        />
        <label htmlFor="password" className="form-label">
          Password
        </label>
        <input
          type="password"
          className="form-control mb-4"
          id="password"
          placeholder="Password"
          onChange={(e) => setPasswordField(e.target.value)}
        />
        <button
          type="button"
          className="btn btn-primary w-100"
          onClick={() => setPassword(passwordField)}
        >
          {intl.formatMessage({ id: "submit" })}
        </button>
      </div>
    );

  return (
    <div style={{ width: "100%", margin: "0 auto" }}>
      {!memberId}
      <TransferDialog
        selMemberId={selectedMemberId}
        selCompanyName={selCompanyName}
        selBalance={selBalance}
        selCompanyCode={selCompanyCode}
        selGameRate={selGameRate}
        selGameUsername={selGameUsername}
        selGameUrl={selGameUrl}
        selGamePass={selGamePass}
        trigger={trigger}
        setGameBalances={setGameBalances}
        handleRemovePromo={handleRemovePromo}
        setTrigger={setTrigger}
        targetBalanceParams={targetBalanceParams}
        gameListQueryParams={queryParams}
        loginid={loginIdRef.current}
      />
      {memberState.overlayLoading && (
        <div
          className="position-fixed w-100 top-0 start-0 end-0 bottom-0 d-flex align-items-center justify-content-center"
          style={{
            scale: "1.5",
            background: "rgba(0,0,0,0.3)",
            zIndex: "9999",
          }}
        >
          <div className="spinner-border" role="status">
            <span className="sr-only"></span>
          </div>
        </div>
      )}
      {/* <CreateGameIdDialog
        selMemberId={selectedMemberId}
        selCompanyCode={selCompanyCode}
        selGameExist={selGameExist}
      /> */}
      <div className={`d-flex align-items-center ${!memberId ? "ps-4" : ""}`}>
        <h2>
          {memberId && (
            <i
              className="bi bi-chevron-left"
              onClick={() => {
                if (pathFromSearch == "yes") {
                  navigate("/searchByUsername", {
                    state: {
                      searchUsername: searchUsername,
                    },
                  });
                } else {
                  navigate(`/accountList`, {
                    state: {
                      returnAgentId: currentAgentId,
                      backToPagination: backToPagination,
                      scrollPosition: scrollPosition,
                      returnUsername: currentUsername,
                    },
                  });
                }
              }}
            ></i>
          )}
        </h2>
        <h2>{intl.formatMessage({ id: "gameList" })}</h2>
      </div>
      <div
        className="d-flex"
        style={{ justifyContent: "space-between", margin: "0 3vh" }}
      >
        {pathFromSearch === "yes" ? (
          <h4>{initialUsername} </h4>
        ) : (
          <h4>{initialAccUsername} </h4>
        )}
      </div>
      <div
        className="row px-0 g-3 mb-4 justify-content-center align-items-center"
        style={{ margin: "0 auto", paddingTop: "10px" }}
      >
        {memberState.memberGameLists &&
        memberState.memberGameLists.memberGameListsV2 ? (
          memberState.memberGameLists.memberGameListsV2.length > 0 &&
          memberState.memberGameLists.memberGameListsV2.map((game, index) => (
            <div className="mb-3 col-6 col-md-3" key={index}>
              <div
                className={`${customCardClass} position-relative text-center gamelistDiv1`}
                {...(memberBalancePermission === "true" &&
                  game.gameexist === "true" && {
                    onClick: (e) => {
                      e.stopPropagation();
                      setSelCompanyName(game.gamename);
                      setSelCompanyCode(game.gamecode);
                      setSelBalance(
                        game.gamebalance === "--"
                          ? gameBalances[game.gamecode]
                          : game.gamebalance
                      );
                      setSelGameRate(game.gamerate);
                      setSelGameUsername(game.gameusername);
                      setSelGamePass(game.gamepassword);
                      setSelGameUrl(game.relateurl);
                      setGameExist(game.gameexist);
                      setTrigger(true);
                    },
                  })}
              >
                {memberBalancePermission === "true" &&
                  game.gameexist === "true" && (
                    <img
                      className="position-absolute"
                      src={toAbsoluteUrl("image/transferIcon.png")}
                      style={{
                        width: "20px",
                        right: "0.01rem",
                        top: "0.5rem",
                        cursor: "pointer",
                      }}
                    />
                  )}

                {game.promotionid !== "" && (
                  <div
                    className="position-absolute text-warning"
                    style={{ left: "0.01rem", top: "0.5rem" }}
                  >
                    {intl.formatMessage({ id: "claim" })}
                  </div>
                )}

                <div className="mb-3 truncate">{game.gamename}</div>
                {game.gameexist === "false" ? (
                  memberState.actionsLoading ? (
                    <div
                      className="spinner-border me-2"
                      role="status"
                      style={{ scale: "0.55" }}
                    >
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  ) : (
                    <div
                      className="fw-bold text-prim card-button-border"
                      onClick={() => {
                        handleCreateClick(game.gamecode);
                      }}
                    >
                      {intl.formatMessage({ id: "create" })}
                    </div>
                  )
                ) : (
                  <div className="fw-bold text-prim">
                    {game.gamebalance === "--"
                      ? gameBalances[game.gamecode]
                      : game.gamebalance}
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          <Skeleton height="600px" className="w-100" />
        )}
      </div>
      <SweetAlert2
        customClass="custom-swal"
        onResolve={() => {
          setSwalProps({});
        }}
        {...swalProps}
      />
    </div>
  );
};
